import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { IconChevronDown, IconLogout, IconSettings, IconUser } from './Icons';

/** "Kavitha Raman" -> "KR". Falls back to one letter, then to a neutral glyph. */
function initialsOf(name: string | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '·';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/**
 * The signed-in account control: who you are, and the way out.
 *
 * Built as a React dropdown rather than Bootstrap's, for three reasons: the admin app
 * never loads Bootstrap's JavaScript, so a `data-bs-toggle` menu is inert there; the
 * keyboard behaviour below (Escape, arrow keys, focus return) is required and Bootstrap's
 * gives only part of it; and one implementation keeps the three portals consistent.
 *
 * Signing out goes through the existing `logout()` on AuthContext, which revokes the
 * refresh token server-side before clearing local state. Nothing here touches tokens.
 */
export function AccountMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close on outside click and on Escape. Escape also returns focus to the trigger, so a
  // keyboard user is not dropped at the top of the document.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Move focus into the menu when it opens by keyboard, so the next Tab stays inside it.
  useEffect(() => {
    if (open) menuRef.current?.querySelector<HTMLElement>('[data-menu-item]')?.focus();
  }, [open]);

  if (!user) return null;

  const signOut = async () => {
    setBusy(true);
    try {
      await logout();
      setOpen(false);
      navigate('/login', { replace: true });
    } finally {
      setBusy(false);
    }
  };

  const businessName = user.businesses[0]?.businessName;
  const businessCount = user.businesses.length;

  return (
    <div className="sf-account" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        className="sf-account__trigger"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="sf-avatar" aria-hidden="true">{initialsOf(user.fullName)}</span>
        <span className="sf-account__who">
          <span className="sf-account__name">{user.fullName}</span>
          {businessName && (
            <span className="sf-account__context">
              {businessName}
              {businessCount > 1 && ` +${businessCount - 1}`}
            </span>
          )}
        </span>
        <IconChevronDown className="sf-account__caret" aria-hidden="true" />
      </button>

      {open && (
        <div className="sf-account__menu" role="menu" ref={menuRef}>
          <div className="sf-account__header">
            <div className="sf-account__name">{user.fullName}</div>
            {user.email && <div className="sf-account__email">{user.email}</div>}
          </div>

          <Link
            className="sf-account__item"
            role="menuitem"
            data-menu-item
            to="/owner/account"
            onClick={() => setOpen(false)}
          >
            <IconUser /> My profile
          </Link>

          <Link
            className="sf-account__item"
            role="menuitem"
            data-menu-item
            to="/owner/account"
            onClick={() => setOpen(false)}
          >
            <IconSettings /> Account settings
          </Link>

          <div className="sf-account__divider" role="separator" />

          <button
            className="sf-account__item sf-account__item--danger"
            role="menuitem"
            data-menu-item
            type="button"
            onClick={signOut}
            disabled={busy}
          >
            <IconLogout /> {busy ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      )}
    </div>
  );
}
